import { uniq } from 'lodash'
import {
  QueryStringFilter,
  QueryStringSort,
} from '../../../models/query-string.model'
import { Page } from '../../../models/util.model'
import { CustomersListingKeys } from '../models/customer-listing.model'
import {
  CustomerSearchField,
  CustomerSearchParams,
  CustomerSortField,
} from '../models/customer-search.model'
import { Customer } from '../models/customer.entity'
import { parseFilters } from '../../../libs/filter.lib'

export function parseCustomerFilters(
  params: CustomerSearchParams,
  filters: CustomerSearchParams,
): CustomerSearchParams {
  return parseFilters(params, filters, customerFilterParams)
}

/**
 * Parse sort params
 * @param params - the search params
 * @param sort - the sort param to parse
 * @returns the customer search params updated
 */
export function customerSortParams(
  params: CustomerSearchParams,
  sort: QueryStringSort<CustomerSortField>,
): CustomerSearchParams {
  const searchParams: CustomerSearchParams = {}

  switch (sort.field) {
    case 'code':
      searchParams.sort = 'code'
      searchParams.order = sort.order
      break
    case 'name':
      searchParams.sort = 'name'
      searchParams.order = sort.order
      break
    case 'businessName':
      searchParams.sort = 'businessName'
      searchParams.order = sort.order
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

/**
 * Parse filter params
 * @param params - the search params
 * @param filter - the filter to parse
 * @returns the customer search params updated
 */
export function customerFilterParams(
  params: CustomerSearchParams,
  filter: QueryStringFilter<CustomerSearchField>,
): CustomerSearchParams {
  const searchParams: CustomerSearchParams = {}

  switch (filter.field) {
    case '_id':
      if (filter.operator === '=') {
        searchParams._id = filter.value
      }
      break
    case 'name':
      if (filter.operator === '=') {
        searchParams.name = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['name:ct'] = filter.value
      }
      break
    case 'businessName':
      if (filter.operator === '=') {
        searchParams.businessName = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['businessName:ct'] = filter.value
      }
      break
    case 'email':
      if (filter.operator === '=') {
        searchParams.email = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['email:ct'] = filter.value
      }
      break
    case 'billingAddress.address1':
      if (filter.operator === '=') {
        searchParams['billingAddress.address1'] = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['billingAddress.address1:ct'] = filter.value
      }
      break
    case 'billingAddress.city':
      if (filter.operator === '=') {
        searchParams['billingAddress.city'] = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['billingAddress.city:ct'] = filter.value
      }
      break
    case 'billingAddress.province':
      if (filter.operator === '=') {
        searchParams['billingAddress.province'] = filter.value
      }
      break
    case 'billingAddress.countryCode':
      if (filter.operator === '=') {
        searchParams['billingAddress.countryCode'] = filter.value
      }
      break
    case 'billingAddress.postalCode':
      if (filter.operator === '=') {
        searchParams['billingAddress.postalCode'] = filter.value
      }
      break
    case 'code':
      if (filter.operator === '=') {
        searchParams.code = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['code:ct'] = filter.value
      }
      break
    case 'alias':
      if (filter.operator === '=') {
        searchParams.alias = filter.value
      }
      break
    case 'priceList':
      if (filter.operator === '=') {
        searchParams.priceList = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['priceList:ct'] = filter.value
      }
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

/**
 * Parse a customer page and product counts to a single page data
 * @param customerPage - the customer page
 * @param productsCounts - the product counts
 * @returns the customers page data
 */
export function parseCustomersPage(
  customerPage: Page<Customer>,
): Page<Customer> {
  const data = customerPage.data.map((b) => ({
    ...b,
  }))
  return {
    totalCount: customerPage.totalCount,
    data,
  }
}

/**
 * Parse customer page keys
 * @param page - the customer page
 * @returns the page listing keys
 */
export function parseCustomerPageKeys(
  page: Page<Customer>,
): CustomersListingKeys {
  return page.data.reduce<CustomersListingKeys>(
    (acc, r) => parseCustomerKeys(r, acc),
    {},
  )
}

/**
 * Parse customer keys
 * @param customer - the customer
 * @param keys - the keys already loaded
 * @returns the customers listing keys
 */
export function parseCustomerKeys(
  customer: Customer,
  keys: CustomersListingKeys = {},
): CustomersListingKeys {
  const countryCodes = uniq([
    ...(keys['countryCodes'] || []),
    ...(customer.billingAddress?.countryCode
      ? [customer.billingAddress?.countryCode]
      : []),
  ])

  return {
    countryCodes,
  }
}
